/**
 * Expanding the seat-map generator's inputs.
 *
 * <p>A real vessel's seat map is not a rectangle — aisles, stairs and wheelchair bays punch holes in
 * it, and a plan with 256 seats can have 66 gaps. Generating the full grid and deleting the holes is
 * the wrong shape of work, so both generator fields take a list rather than a count: run Generate
 * once per band of rows that share a seat set, and the existing skip-what-exists rule makes the runs
 * additive.</p>
 */

const NUMERIC_RANGE = /^(\d+)\s*-\s*(\d+)$/;
const LETTER_RANGE = /^([A-Za-z])\s*-\s*([A-Za-z])$/;

/** Every integer from `from` to `to` inclusive, counting in whichever direction that implies. */
const numericRange = (from: number, to: number): string[] => {
	const step = from <= to ? 1 : -1;
	const values: string[] = [];

	for (let value = from; step > 0 ? value <= to : value >= to; value += step) {
		values.push(String(value));
	}

	return values;
};

/**
 * Every letter from `from` to `to` inclusive. Case follows `from`, so `a-f` gives lower case and
 * `A-F` upper — the label is printed on the vessel and the two are not interchangeable.
 */
const letterRange = (from: string, to: string): string[] => {
	const upper = from === from.toUpperCase();
	const start = from.toUpperCase().charCodeAt(0);
	const end = to.toUpperCase().charCodeAt(0);
	const step = start <= end ? 1 : -1;
	const values: string[] = [];

	for (let code = start; step > 0 ? code <= end : code >= end; code += step) {
		const letter = String.fromCharCode(code);
		values.push(upper ? letter : letter.toLowerCase());
	}

	return values;
};

export interface ExpandOptions {
	/**
	 * When a specification is a single positive integer, read it as "this many, numbered from 1"
	 * rather than as one label. On for the seat field, where `4` has always meant four seats; off for
	 * rows, where a row can legitimately be called `4`.
	 */
	countShorthand?: boolean;
}

/**
 * Expands one generator field into the labels it names.
 *
 * <p>Accepts explicit labels (`A,B,C`), ranges in either direction (`1-12`, `23-13`, `A-N`), and any
 * mix of the two (`23-13,12-1`). Order is the order given, because it becomes the seats' display
 * order — a plan that reads 23 down to 13 across the deck should be entered that way.</p>
 *
 * <p>Duplicates are dropped, keeping the first occurrence, so an overlapping range does not produce
 * two of the same seat for the server to reject.</p>
 */
export const expandLabelSpec = (spec: string, { countShorthand = false }: ExpandOptions = {}): string[] => {
	const tokens = spec
		.split(',')
		.map(token => token.trim())
		.filter(Boolean);

	if (countShorthand && tokens.length === 1 && /^\d+$/.test(tokens[0])) {
		const count = Number(tokens[0]);
		return count > 0 ? numericRange(1, count) : [];
	}

	const labels = tokens.flatMap(token => {
		const numeric = NUMERIC_RANGE.exec(token);
		if (numeric) {
			return numericRange(Number(numeric[1]), Number(numeric[2]));
		}

		const letters = LETTER_RANGE.exec(token);
		if (letters) {
			return letterRange(letters[1], letters[2]);
		}

		return [token];
	});

	return [...new Set(labels)];
};

/** Row labels named by the generator's `Rows` field. */
export const expandRowSpec = (spec: string): string[] => expandLabelSpec(spec);

/** Seat numbers named by the generator's `Seats` field, where a bare number still means a count. */
export const expandSeatSpec = (spec: string): string[] => expandLabelSpec(spec, { countShorthand: true });

/** One exclusion from a generator run: a seat number, optionally confined to a single row. */
export interface SeatExclusion {
	/** Undefined means "this seat number in every row of the run". */
	rowLabel?: string;
	seatNumber: string;
}

/**
 * Seats to leave out of a generator run.
 *
 * <p>A bare token excludes that seat number from every row in the run (`16,21` for a pair of
 * aisles). Prefixing it with a row confines it to that row (`C:14` for the single hole where a
 * wheelchair bay sits), and ranges work in both forms (`2-4`, `C:13-14`).</p>
 */
export const expandExclusionSpec = (spec: string): SeatExclusion[] =>
	spec
		.split(',')
		.map(token => token.trim())
		.filter(Boolean)
		.flatMap(token => {
			const separator = token.indexOf(':');
			if (separator < 0) {
				return expandLabelSpec(token).map(seatNumber => ({ seatNumber }));
			}

			const rowLabel = token.slice(0, separator).trim();
			const seats = expandLabelSpec(token.slice(separator + 1));
			return rowLabel ? seats.map(seatNumber => ({ rowLabel, seatNumber })) : seats.map(seatNumber => ({ seatNumber }));
		});

/** Whether a generator run should skip this cell. */
export const isExcluded = (rowLabel: string, seatNumber: string, exclusions: SeatExclusion[]): boolean =>
	exclusions.some(
		exclusion =>
			exclusion.seatNumber === seatNumber
			&& (exclusion.rowLabel === undefined || exclusion.rowLabel === rowLabel),
	);
